import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { Event, EventFormData, Registration, RegistrationFormData, Feedback, FeedbackFormData } from '../types';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { eventStreamService, EventStreamUpdate } from '../services/eventStream';

interface EventState {
  events: Event[];
  registrations: Registration[];
  feedback: Feedback[];
  isLoading: boolean;
  error: string | null;
}

type EventAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_EVENTS'; payload: Event[] }
  | { type: 'ADD_EVENT'; payload: Event }
  | { type: 'UPDATE_EVENT'; payload: Event }
  | { type: 'DELETE_EVENT'; payload: string }
  | { type: 'UPDATE_EVENT_CAPACITY'; payload: { eventId: string; currentAttendees: number } }
  | { type: 'ADD_REGISTRATION'; payload: Registration }
  | { type: 'UPDATE_REGISTRATION'; payload: Registration }
  | { type: 'CANCEL_REGISTRATION'; payload: string }
  | { type: 'SET_REGISTRATIONS'; payload: Registration[] }
  | { type: 'ADD_FEEDBACK'; payload: Feedback }
  | { type: 'SET_FEEDBACK'; payload: Feedback[] };

const initialState: EventState = {
  events: [],
  registrations: [],
  feedback: [],
  isLoading: false,
  error: null,
};

const eventReducer = (state: EventState, action: EventAction): EventState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_EVENTS':
      return { ...state, events: action.payload };
    case 'ADD_EVENT':
      return { ...state, events: [...state.events, action.payload] };
    case 'UPDATE_EVENT':
      return {
        ...state,
        events: state.events.map((e) =>
          e.id === action.payload.id ? action.payload : e
        ),
      };
    case 'DELETE_EVENT':
      return {
        ...state,
        events: state.events.filter((e) => e.id !== action.payload),
      };
    case 'UPDATE_EVENT_CAPACITY':
      return {
        ...state,
        events: state.events.map((e) =>
          e.id === action.payload.eventId
            ? { ...e, currentAttendees: action.payload.currentAttendees }
            : e
        ),
      };
    case 'ADD_REGISTRATION':
      return {
        ...state,
        registrations: [...state.registrations, action.payload],
        events: state.events.map((e) =>
          e.id === action.payload.eventId
            ? { ...e, currentAttendees: e.currentAttendees + action.payload.attendeeCount }
            : e
        ),
      };
    case 'UPDATE_REGISTRATION':
      const oldReg = state.registrations.find((r) => r.id === action.payload.id);
      const attendeeDelta = oldReg ? action.payload.attendeeCount - oldReg.attendeeCount : 0;
      return {
        ...state,
        registrations: state.registrations.map((r) =>
          r.id === action.payload.id ? action.payload : r
        ),
        events: state.events.map((e) =>
          e.id === action.payload.eventId
            ? { ...e, currentAttendees: e.currentAttendees + attendeeDelta }
            : e
        ),
      };
    case 'CANCEL_REGISTRATION':
      const registration = state.registrations.find((r) => r.id === action.payload);
      return {
        ...state,
        registrations: state.registrations.map((r) =>
          r.id === action.payload
            ? { ...r, status: 'cancelled' as const, cancelledAt: new Date().toISOString() }
            : r
        ),
        events: registration
          ? state.events.map((e) =>
              e.id === registration.eventId
                ? { ...e, currentAttendees: Math.max(0, e.currentAttendees - registration.attendeeCount) }
                : e
            )
          : state.events,
      };
    case 'SET_REGISTRATIONS':
      return { ...state, registrations: action.payload };
    case 'ADD_FEEDBACK':
      return { ...state, feedback: [...state.feedback, action.payload] };
    case 'SET_FEEDBACK':
      return { ...state, feedback: action.payload };
    default:
      return state;
  }
};

// Helper to normalise event dates from API (DateTime -> YYYY-MM-DD string)
function normalizeEvent(e: any): Event {
  return {
    ...e,
    date: typeof e.date === 'string' && e.date.includes('T') ? e.date.split('T')[0] : e.date,
    registrationDeadline:
      typeof e.registrationDeadline === 'string' && e.registrationDeadline.includes('T')
        ? e.registrationDeadline.split('T')[0]
        : e.registrationDeadline,
  };
}

interface ModifyRegistrationData {
  familyMemberIds: string[];
  specialRequirements?: string;
}

interface EventContextType extends EventState {
  getEvent: (id: string) => Event | undefined;
  fetchEvents: (silent?: boolean) => Promise<void>;
  createEvent: (data: EventFormData) => Promise<Event>;
  updateEvent: (id: string, data: EventFormData) => Promise<Event>;
  deleteEvent: (id: string) => Promise<void>;
  registerForEvent: (data: RegistrationFormData, userId: string) => Promise<Registration>;
  cancelRegistration: (registrationId: string) => Promise<void>;
  modifyRegistration: (registrationId: string, data: ModifyRegistrationData) => Promise<Registration>;
  cancelAttendee: (attendeeId: string) => Promise<{ type: string; registration: Registration }>;
  getUserRegistrations: (userId: string) => Registration[];
  getEventRegistrations: (eventId: string) => Registration[];
  submitFeedback: (data: FeedbackFormData, userId: string) => Promise<Feedback>;
  getUserFeedback: (userId: string) => Feedback[];
  getEventFeedback: (eventId: string) => Feedback[];
  getAllFeedback: () => Feedback[];
  hasUserSubmittedFeedback: (eventId: string, userId: string) => boolean;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

interface EventProviderProps {
  children: ReactNode;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(eventReducer, initialState);
  const { isAuthenticated, user } = useAuth();

  // Fetch events on mount and when auth state changes
  const fetchEvents = useCallback(async (silent = false) => {
    if (!silent) dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Admin gets all events including inactive; regular users get active events with past events hidden
      const queryParams = user?.role === 'admin' ? '?includeInactive=true' : '?hidePast=true';
      const res = await api.get(`/events${queryParams}`);
      const events = res.data.map(normalizeEvent);
      dispatch({ type: 'SET_EVENTS', payload: events });
    } catch (err: any) {
      if (!silent) dispatch({ type: 'SET_ERROR', payload: err.response?.data?.error || 'Failed to load events' });
    } finally {
      if (!silent) dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [user?.role]);

  // Fetch user registrations when authenticated (hide past events)
  const fetchMyRegistrations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/registrations/my?hidePast=true');
      dispatch({ type: 'SET_REGISTRATIONS', payload: res.data });
    } catch {
      // Silently fail - registrations will be empty
    }
  }, [isAuthenticated]);

  // Fetch feedback when authenticated
  const fetchMyFeedback = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      if (user?.role === 'admin') {
        const res = await api.get('/feedback');
        dispatch({ type: 'SET_FEEDBACK', payload: res.data });
      } else {
        const res = await api.get('/feedback/my');
        dispatch({ type: 'SET_FEEDBACK', payload: res.data });
      }
    } catch {
      // Silently fail
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetchMyRegistrations();
  }, [fetchMyRegistrations]);

  useEffect(() => {
    fetchMyFeedback();
  }, [fetchMyFeedback]);

  // Subscribe to real-time SSE updates for instant event changes
  useEffect(() => {
    const unsubscribe = eventStreamService.subscribe((update: EventStreamUpdate) => {
      switch (update.type) {
        case 'event_created':
          if (update.data) {
            const event = normalizeEvent(update.data);
            dispatch({ type: 'ADD_EVENT', payload: event });
          }
          break;
        case 'event_updated':
          if (update.data) {
            const event = normalizeEvent(update.data);
            dispatch({ type: 'UPDATE_EVENT', payload: event });
          }
          break;
        case 'event_deleted':
          if (update.eventId) {
            dispatch({ type: 'DELETE_EVENT', payload: update.eventId });
          }
          break;
        case 'capacity_changed':
          if (update.eventId && update.data) {
            dispatch({
              type: 'UPDATE_EVENT_CAPACITY',
              payload: {
                eventId: update.eventId,
                currentAttendees: update.data.currentAttendees,
              },
            });
          }
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  // Hybrid real-time approach: SSE for instant push + 10s background sync as safety net
  // This ensures data is always fresh within 10 seconds, even if SSE misses an update
  useEffect(() => {
    const interval = setInterval(() => {
      // Always sync data every 10 seconds (silent - no loading spinner)
      // This catches any updates that SSE might have missed
      fetchEvents(true);
      if (isAuthenticated) {
        fetchMyRegistrations();
      }

      // If SSE is disconnected, try to reconnect
      if (!eventStreamService.isConnected()) {
        eventStreamService.tryReconnect();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchEvents, fetchMyRegistrations, isAuthenticated]);

  // PWA optimization: Instantly refresh data when app returns to foreground
  // This eliminates the delay when returning from background/minimized state
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // App just became visible - immediately refresh data
        fetchEvents(true);
        if (isAuthenticated) {
          fetchMyRegistrations();
        }
        // Force SSE reconnection if disconnected
        if (!eventStreamService.isConnected()) {
          eventStreamService.tryReconnect();
        }
      }
    };

    // Also handle when app regains focus (covers more PWA scenarios)
    const handleFocus = () => {
      fetchEvents(true);
      if (isAuthenticated) {
        fetchMyRegistrations();
      }
      if (!eventStreamService.isConnected()) {
        eventStreamService.tryReconnect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchEvents, fetchMyRegistrations, isAuthenticated]);

  const getEvent = (id: string): Event | undefined => {
    return state.events.find((e) => e.id === id);
  };

  const createEvent = async (data: EventFormData): Promise<Event> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await api.post('/events', data);
      const event = normalizeEvent(res.data);
      dispatch({ type: 'ADD_EVENT', payload: event });
      return event;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to create event');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateEvent = async (id: string, data: EventFormData): Promise<Event> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await api.put(`/events/${id}`, data);
      const event = normalizeEvent(res.data);
      dispatch({ type: 'UPDATE_EVENT', payload: event });
      return event;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to update event');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const deleteEvent = async (id: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await api.delete(`/events/${id}`);
      dispatch({ type: 'DELETE_EVENT', payload: id });
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to delete event');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const registerForEvent = async (
    data: RegistrationFormData,
    userId: string
  ): Promise<Registration> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Plan 4.3 — backend defaults guestIds to [] via Zod, but normalising
      // here too keeps the wire shape predictable for logging/snapshots.
      const body = { ...data, guestIds: data.guestIds ?? [] };
      const res = await api.post('/registrations', body);
      const registration = res.data;
      dispatch({ type: 'ADD_REGISTRATION', payload: registration });
      return registration;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to register');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const cancelRegistration = async (registrationId: string): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      await api.put(`/registrations/${registrationId}/cancel`);
      dispatch({ type: 'CANCEL_REGISTRATION', payload: registrationId });
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to cancel registration');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const modifyRegistration = async (
    registrationId: string,
    data: ModifyRegistrationData
  ): Promise<Registration> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await api.put(`/registrations/${registrationId}/modify`, data);
      const registration = res.data;
      dispatch({ type: 'UPDATE_REGISTRATION', payload: registration });
      return registration;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to modify registration');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const cancelAttendee = async (
    attendeeId: string
  ): Promise<{ type: string; registration: Registration }> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await api.put(`/registrations/attendee/${attendeeId}/cancel`);
      const { type, registration } = res.data;
      if (type === 'registration_cancelled') {
        dispatch({ type: 'CANCEL_REGISTRATION', payload: registration.id });
      } else {
        dispatch({ type: 'UPDATE_REGISTRATION', payload: registration });
      }
      return res.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to cancel attendee');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getUserRegistrations = (userId: string): Registration[] => {
    return state.registrations.filter((r) => r.userId === userId);
  };

  const getEventRegistrations = (eventId: string): Registration[] => {
    return state.registrations.filter((r) => r.eventId === eventId);
  };

  const submitFeedback = async (
    data: FeedbackFormData,
    userId: string
  ): Promise<Feedback> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await api.post('/feedback', data);
      const feedback = res.data;
      dispatch({ type: 'ADD_FEEDBACK', payload: feedback });
      return feedback;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to submit feedback');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getUserFeedback = (userId: string): Feedback[] => {
    return state.feedback.filter((f) => f.userId === userId);
  };

  const getEventFeedback = (eventId: string): Feedback[] => {
    return state.feedback.filter((f) => f.eventId === eventId);
  };

  const getAllFeedback = (): Feedback[] => {
    return state.feedback;
  };

  const hasUserSubmittedFeedback = (eventId: string, userId: string): boolean => {
    return state.feedback.some((f) => f.eventId === eventId && f.userId === userId);
  };

  return (
    <EventContext.Provider
      value={{
        ...state,
        getEvent,
        fetchEvents,
        createEvent,
        updateEvent,
        deleteEvent,
        registerForEvent,
        cancelRegistration,
        modifyRegistration,
        cancelAttendee,
        getUserRegistrations,
        getEventRegistrations,
        submitFeedback,
        getUserFeedback,
        getEventFeedback,
        getAllFeedback,
        hasUserSubmittedFeedback,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

export const useEvents = (): EventContextType => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error('useEvents must be used within an EventProvider');
  }
  return context;
};

export default EventContext;
